/**
 * Assign Collection Strategy Dialog Component
 * Displays a confirmation dialog for bulk assigning collection strategies to customers
 * Shows counts of new assignments vs. updates to existing strategies
 */

import React from "react";
import { AlertCircle, CheckCircle2, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

interface Customer {
  id: string;
  name: string;
  accountNumber: string;
  currency: "USD" | "EUR" | "GBP" | "CAD" | "AUD";
  outstanding: number;
  overdue: number;
  creditLimit: number;
  dso: number;
  risk: "high" | "medium" | "low";
  lastPayment: string;
  contact: string;
  phone: string;
  address: string;
  paymentTerms: "Net 15" | "Net 30" | "Net 45" | "Net 60" | "Net 90";
  collectionStrategyId: string;
}

interface CollectionStrategy {
  id: string;
  name: string;
  type: string;
  description: string;
}

interface AssignStrategyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCustomers: Customer[];
  strategies: CollectionStrategy[];
  onConfirm: (strategyId: string) => void;
}

export function AssignStrategyDialog({
  open,
  onOpenChange,
  selectedCustomers,
  strategies,
  onConfirm,
}: AssignStrategyDialogProps) {
  const [selectedStrategyId, setSelectedStrategyId] = React.useState<string>("");

  // Calculate counts
  const totalSelected = selectedCustomers.length;
  const customersWithNoStrategy = selectedCustomers.filter(
    (c) => !c.collectionStrategyId || c.collectionStrategyId === ""
  ).length;
  const customersWithExistingStrategy = totalSelected - customersWithNoStrategy;

  // Get selected strategy details
  const selectedStrategy = strategies.find((s) => s.id === selectedStrategyId);

  const handleConfirm = () => {
    if (selectedStrategyId) {
      onConfirm(selectedStrategyId);
      setSelectedStrategyId("");
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setSelectedStrategyId("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Users className="h-5 w-5 text-primary" />
            Assign Collection Strategy
          </DialogTitle>
          <DialogDescription>
            Select a collection strategy to assign to the selected customer accounts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Strategy Selection */}
          <div className="space-y-2">
            <Label htmlFor="strategy-select">Collection Strategy</Label>
            <Select value={selectedStrategyId} onValueChange={setSelectedStrategyId}>
              <SelectTrigger id="strategy-select">
                <SelectValue placeholder="Select a strategy..." />
              </SelectTrigger>
              <SelectContent>
                {strategies.map((strategy) => (
                  <SelectItem key={strategy.id} value={strategy.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{strategy.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {strategy.type}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedStrategy && (
              <p className="text-sm text-muted-foreground mt-2">
                {selectedStrategy.description}
              </p>
            )}
          </div>

          <Separator />

          {/* Impact Summary */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-semibold text-sm">Impact Summary</h4>
            </div>

            <div className="grid gap-3">
              {/* Total Selected */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Total Selected</span>
                </div>
                <Badge variant="secondary" className="text-base font-semibold">
                  {totalSelected}
                </Badge>
              </div>

              {/* New Assignments */}
              {customersWithNoStrategy > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-500" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-green-900 dark:text-green-100">
                        New Strategy Assignments
                      </span>
                      <span className="text-xs text-green-700 dark:text-green-400">
                        Customers with no existing strategy
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-green-600 hover:bg-green-700 text-white text-base font-semibold">
                    {customersWithNoStrategy}
                  </Badge>
                </div>
              )}

              {/* Strategy Updates */}
              {customersWithExistingStrategy > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-amber-900 dark:text-amber-100">
                        Strategy Updates
                      </span>
                      <span className="text-xs text-amber-700 dark:text-amber-400">
                        Customers with existing strategy will be updated
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-amber-600 hover:bg-amber-700 text-white text-base font-semibold">
                    {customersWithExistingStrategy}
                  </Badge>
                </div>
              )}
            </div>

            {/* Information Note */}
            <div className="flex gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-900 dark:text-blue-100">
                <strong>Note:</strong> This action will immediately assign the selected collection strategy 
                to all {totalSelected} customer{totalSelected !== 1 ? "s" : ""}. 
                {customersWithExistingStrategy > 0 && (
                  <> Existing strategies will be replaced with the new assignment.</>
                )}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedStrategyId}
            className="gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            Confirm Assignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
